/**
 * useComplaints — Issue #175.
 *
 * Admin-side hook for the complaint / dispute work order module.
 *
 * 红线：
 *   - 只使用 generated @jinzhuan/api-client；不手写 fetch。
 *   - 所有 mutation 必须带 expectedVersion 走乐观锁。
 *   - 错误以后端返回值转换；不静默吞掉 HTTP 错误。
 *   - 不在前端伪造已升级/已闭环的状态；mutation 必须使用后端返回值刷新本地状态。
 */
import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 投诉工单端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`（Issue #226）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

export type ComplaintWorkOrderDto =
  ApiComponents['schemas']['ComplaintWorkOrderDto'];
export type ComplaintWorkOrderListDto =
  ApiComponents['schemas']['ComplaintWorkOrderListDto'];
export type ComplaintWorkOrderEventDto =
  ApiComponents['schemas']['ComplaintWorkOrderEventDto'];
export type CreateComplaintWorkOrderDto =
  ApiComponents['schemas']['CreateComplaintWorkOrderDto'];
export type AcceptComplaintWorkOrderDto =
  ApiComponents['schemas']['AcceptComplaintWorkOrderDto'];
export type StartComplaintWorkOrderDto =
  ApiComponents['schemas']['StartComplaintWorkOrderDto'];
export type EscalateComplaintWorkOrderDto =
  ApiComponents['schemas']['EscalateComplaintWorkOrderDto'];
export type SubmitComplaintForReviewDto =
  ApiComponents['schemas']['SubmitComplaintForReviewDto'];
export type ReviewComplaintWorkOrderDto =
  ApiComponents['schemas']['ReviewComplaintWorkOrderDto'];
export type CloseComplaintWorkOrderDto =
  ApiComponents['schemas']['CloseComplaintWorkOrderDto'];
export type RejectComplaintWorkOrderDto =
  ApiComponents['schemas']['RejectComplaintWorkOrderDto'];

export type ComplaintWorkOrderStatus = ComplaintWorkOrderDto['status'];
export type ComplaintWorkOrderType = ComplaintWorkOrderDto['complaintType'];
export type ComplaintWorkOrderRiskLevel = ComplaintWorkOrderDto['riskLevel'];

export const COMPLAINT_STATE_LIST: readonly ComplaintWorkOrderStatus[] = [
  'submitted',
  'accepted',
  'processing',
  'escalated',
  'pending_review',
  'closed',
  'rejected',
];

export const COMPLAINT_STATE_LABELS: Record<ComplaintWorkOrderStatus, string> = {
  submitted: '已登记',
  accepted: '已受理',
  processing: '处理中',
  escalated: '已升级',
  pending_review: '待复盘',
  closed: '已闭环',
  rejected: '无效投诉',
};

export const COMPLAINT_TYPE_LIST: readonly ComplaintWorkOrderType[] = [
  'service_quality',
  'medical_dispute',
  'scheduling',
  'finance',
  'privacy',
  'other',
];

export const COMPLAINT_TYPE_LABELS: Record<ComplaintWorkOrderType, string> = {
  service_quality: '服务质量',
  medical_dispute: '医疗纠纷',
  scheduling: '排期相关',
  finance: '财务争议',
  privacy: '隐私安全',
  other: '其他',
};

export const COMPLAINT_RISK_LIST: readonly ComplaintWorkOrderRiskLevel[] = [
  'low',
  'medium',
  'high',
  'critical',
];

export const COMPLAINT_RISK_LABELS: Record<ComplaintWorkOrderRiskLevel, string> = {
  low: '低',
  medium: '中',
  high: '高',
  critical: '极高',
};

export interface ComplaintApiError {
  message: string;
  code?: string;
  status?: number;
}

function toApiError(err: unknown, httpStatus?: number): ComplaintApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return {
      message: '会话已失效，请重新登录后再试',
      code: 'UNAUTHENTICATED',
      status,
    };
  }
  if (status === 409) {
    return {
      message: '记录已被其他操作修改或状态不符，请刷新后重试',
      code: 'CONFLICT',
      status,
    };
  }
  if (status === 403) {
    return { message: '没有权限执行此操作', code: 'FORBIDDEN', status };
  }
  if (status === 400) {
    return {
      message: '请求参数不合法，请检查必填项与关联 ID',
      code: 'BAD_REQUEST',
      status,
    };
  }
  if (status === 404) {
    return { message: '投诉记录不存在或不可访问', code: 'NOT_FOUND', status };
  }
  if (err instanceof Error) {
    return { message: err.message, status };
  }
  return { message: String(err), status };
}

export interface ListFilter {
  status?: ComplaintWorkOrderStatus;
  complaintType?: ComplaintWorkOrderType;
  riskLevel?: ComplaintWorkOrderRiskLevel;
  customerId?: string;
  orderId?: string;
  orderProjectId?: string;
  serviceUserId?: string;
  assigneeId?: string;
  escalated?: boolean;
  page?: number;
  limit?: number;
}

export function useComplaints() {
  const [items, setItems] = useState<ComplaintWorkOrderDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ComplaintApiError | null>(null);

  const fetchComplaints = useCallback(async (filter?: ListFilter) => {
    setLoading(true);
    setError(null);
    try {
      const query: Record<string, unknown> = {};
      if (filter?.status) query.status = filter.status;
      if (filter?.complaintType) query.complaintType = filter.complaintType;
      if (filter?.riskLevel) query.riskLevel = filter.riskLevel;
      if (filter?.customerId) query.customerId = filter.customerId;
      if (filter?.orderId) query.orderId = filter.orderId;
      if (filter?.orderProjectId) query.orderProjectId = filter.orderProjectId;
      if (filter?.serviceUserId) query.serviceUserId = filter.serviceUserId;
      if (filter?.assigneeId) query.assigneeId = filter.assigneeId;
      if (filter?.escalated !== undefined) query.escalated = filter.escalated;
      if (filter?.page) query.page = filter.page;
      if (filter?.limit) query.limit = filter.limit;
      const { data, error: apiErr, response } = await apiClient.GET(
        '/api/v1/complaints',
        { params: { query } },
      );
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return null;
      }
      if (data) {
        setItems([...data.items]);
        setTotal(data.total);
      }
      return data ?? null;
    } catch (e) {
      setError(toApiError(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(
    async (id: string): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/complaints/{id}',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const fetchEvents = useCallback(
    async (id: string): Promise<ComplaintWorkOrderEventDto[] | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET(
          '/api/v1/complaints/{id}/events',
          { params: { path: { id } } },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ? [...data] : null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const createComplaint = useCallback(
    async (dto: CreateComplaintWorkOrderDto): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints',
          { body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const acceptComplaint = useCallback(
    async (
      id: string,
      dto: AcceptComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/accept',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const startComplaint = useCallback(
    async (
      id: string,
      dto: StartComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/start',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const escalateComplaint = useCallback(
    async (
      id: string,
      dto: EscalateComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/escalate',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const submitForReview = useCallback(
    async (
      id: string,
      dto: SubmitComplaintForReviewDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/submit-review',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const reviewComplaint = useCallback(
    async (
      id: string,
      dto: ReviewComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/review',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const closeComplaint = useCallback(
    async (
      id: string,
      dto: CloseComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/close',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  const rejectComplaint = useCallback(
    async (
      id: string,
      dto: RejectComplaintWorkOrderDto,
    ): Promise<ComplaintWorkOrderDto | null> => {
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST(
          '/api/v1/complaints/{id}/reject',
          { params: { path: { id } }, body: dto },
        );
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      }
    },
    [],
  );

  return {
    items,
    total,
    loading,
    error,
    fetchComplaints,
    fetchDetail,
    fetchEvents,
    createComplaint,
    acceptComplaint,
    startComplaint,
    escalateComplaint,
    submitForReview,
    reviewComplaint,
    closeComplaint,
    rejectComplaint,
  };
}
